import * as actions from './actions';

import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { AjaxResponse } from 'rxjs/ajax';
import { Epic } from 'redux-observable';
import { GetResourceAvailableDatesAPIResponse } from './types';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { isActionOf } from 'typesafe-actions';
import { of, takeUntil } from 'rxjs';
import { transformErrorIntoSerializableError } from 'store/utils';
import { ResourceBookingServiceClient } from 'store/api-client/resource-booking-service-client';
import { ENABLE_RESOURCE_BOOKING_SERVICE } from 'store/featureFlags/consts';
import { ResourceBookingEnum } from 'store/app-instance-configs/enums';

export const getResourceAvailableDatesEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getResourceAvailableDates.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const useService = state.featureFlags?.flags?.[ENABLE_RESOURCE_BOOKING_SERVICE];
      const resourceUuid = state.resource?.resource?.uuid;
      const currentConfig = state.appInstanceConfigs?.app_instance_configs?.find(
        config => config.uuid === state.appInstanceConfigs.currentAppInstanceConfigUuid,
      );
      const isNativeAdapter = currentConfig?.vertical_adapter?.name === ResourceBookingEnum.NativeAdapter;

      if (useService && isNativeAdapter && resourceUuid) {
        const serviceClient = new ResourceBookingServiceClient(state.config.apiUrl, state.config.authToken);

        return serviceClient.getResourceAvailableDates(resourceUuid, payload).pipe(
          map(({ data }) =>
            actions.getResourceAvailableDates.success({
              indexOfMonth: payload.indexOfMonth.toString(),
              available_dates: data.available_dates,
            }),
          ),
          catchError((error: Error) =>
            of(actions.getResourceAvailableDates.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
      }

      return apiClient(state)
        .getResourceAvailableDates(payload)
        .pipe(
          map(({ response }: AjaxResponse<GetResourceAvailableDatesAPIResponse>) => {
            return actions.getResourceAvailableDates.success({
              indexOfMonth: payload.indexOfMonth.toString(),
              available_dates: response.data.available_dates,
            });
          }),
          catchError((error: Error) =>
            of(actions.getResourceAvailableDates.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );

export const getResourceInfiniteAvailableDatesEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getResourceInfiniteAvailableDates.request)),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const useService = state.featureFlags?.flags?.[ENABLE_RESOURCE_BOOKING_SERVICE];
      const resourceUuid = state.resource?.resource?.uuid;
      const currentConfig = state.appInstanceConfigs?.app_instance_configs?.find(
        config => config.uuid === state.appInstanceConfigs.currentAppInstanceConfigUuid,
      );
      const isNativeAdapter = currentConfig?.vertical_adapter?.name === ResourceBookingEnum.NativeAdapter;

      if (useService && isNativeAdapter && resourceUuid) {
        const serviceClient = new ResourceBookingServiceClient(state.config.apiUrl, state.config.authToken);

        return serviceClient.getResourceAvailableDates(resourceUuid, payload).pipe(
          takeUntil(action$.pipe(filter(isActionOf(actions.resetResourceAvailableDates)))),
          map(({ data }) =>
            actions.getResourceInfiniteAvailableDates.success({
              indexOfMonth: payload.indexOfMonth.toString(),
              available_dates: data.available_dates,
            }),
          ),
          catchError((error: Error) =>
            of(
              actions.getResourceInfiniteAvailableDates.failure({ error: transformErrorIntoSerializableError(error) }),
            ),
          ),
        );
      }

      return apiClient(state)
        .getResourceAvailableDates(payload)
        .pipe(
          takeUntil(action$.pipe(filter(isActionOf(actions.resetResourceAvailableDates)))),
          map(({ response }: AjaxResponse<GetResourceAvailableDatesAPIResponse>) => {
            return actions.getResourceInfiniteAvailableDates.success({
              indexOfMonth: payload.indexOfMonth.toString(),
              available_dates: response.data.available_dates,
            });
          }),
          catchError((error: Error) =>
            of(
              actions.getResourceInfiniteAvailableDates.failure({ error: transformErrorIntoSerializableError(error) }),
            ),
          ),
        );
    }),
  );
